import type { DropDownFieldValueObject } from "types";

export type SortableDropdownOption = Omit<DropDownFieldValueObject, "id"> & {
  id: string | number;
};

export function sortDropDownOptions<T extends SortableDropdownOption>(
  options: T[],
): T[] {
  return options
    .map(
      (option) =>
        ({
          ...option,
          id:
            typeof option.id === "string" ? option.id.toLowerCase() : option.id,
        }) as T,
    )
    .sort((optionA, optionB) => optionA.position - optionB.position);
}

export function collectionLabelsEqual(
  firstLabel: string | undefined | null,
  secondLabel: string | undefined | null,
): boolean {
  return (
    String(firstLabel ?? "")
      .trim()
      .toLowerCase() ===
    String(secondLabel ?? "")
      .trim()
      .toLowerCase()
  );
}

export function isResolvedCollectionValue(value: {
  id?: unknown;
  label?: unknown;
  value?: unknown;
}): boolean {
  const id = String(value?.id ?? "").trim();
  const label = String(value?.label ?? value?.value ?? "").trim();

  return id !== "" && label !== "";
}

export function normalizeCountryCollectionValue(country: unknown): {
  id: string;
  label: string;
} {
  if (country === null || country === undefined) {
    return { id: "", label: "" };
  }

  if (typeof country === "string") {
    const label = country.trim();

    return label ? { id: label, label } : { id: "", label: "" };
  }

  if (typeof country === "object") {
    const value = country as Record<string, unknown>;
    const id = String(value.id ?? value.value ?? "").trim();
    const label = String(value.label ?? value.name ?? value.value ?? id).trim();

    return { id, label };
  }

  return { id: "", label: "" };
}

export function findSingleExactCollectionOptionByLabel<
  T extends { label?: string },
>(label: string, options: T[] | null | undefined): T | null {
  if (!options?.length) {
    return null;
  }

  const matches = options.filter((option) =>
    collectionLabelsEqual(option.label, label),
  );

  return matches.length === 1 ? matches[0] : null;
}
